import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * MarkdownScrollContainer
 *
 * Renderiza conteúdo rolável e dispara `onReachEnd` quando o usuário rola até
 * o fim (com tolerância de 4px). Também dispara imediatamente caso o conteúdo
 * não seja rolável (cabe inteiro na viewport).
 *
 * Usa ResizeObserver para re-verificar quando o layout se resolver
 * (ex: imagens acima do container terminam de carregar).
 *
 * @param {Object} props
 * @param {() => void} props.onReachEnd
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 * @param {string|number} [props.resetKey] - Mudar este valor força reset do estado "lido"
 */
export default function MarkdownScrollContainer({ onReachEnd, children, className = '', resetKey }) {
    const ref = useRef(null);
    const [reached, setReached] = useState(false);
    const reachedRef = useRef(false);

    // Sincroniza ref para uso dentro de callbacks sem stale closure
    reachedRef.current = reached;

    const fireReachEnd = useCallback(() => {
        if (!reachedRef.current) {
            setReached(true);
            onReachEnd?.();
        }
    }, [onReachEnd]);

    const checkEnd = useCallback(() => {
        const el = ref.current;
        if (!el || reachedRef.current) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 4) {
            fireReachEnd();
        }
    }, [fireReachEnd]);

    // Reset quando muda o comunicado (resetKey)
    useEffect(() => {
        setReached(false);
        reachedRef.current = false;
        // Após reset, verificar imediatamente se conteúdo novo já cabe sem scroll
        requestAnimationFrame(() => checkEnd());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey]);

    // ResizeObserver: re-verifica quando dimensões do container mudam
    // (flex layout resolve após imagem acima carregar)
    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const observer = new ResizeObserver(() => checkEnd());
        observer.observe(el);

        // Verificação inicial após mount
        requestAnimationFrame(() => checkEnd());

        return () => observer.disconnect();
    }, [checkEnd]);

    return (
        <div ref={ref} onScroll={checkEnd} className={className}>
            {children}
        </div>
    );
}
